import {
  Compass, ShieldCheck, HeartHandshake, BookOpen, Leaf,
  Sparkles, Users, MessageCircle,
} from "lucide-react";
import useSeo, { PROD_DOMAIN } from "../lib/seo";
import {
  TrustPage, TrustHero, TrustSection, IconCardGrid,
  ClosingCta,
} from "../components/TrustLayout";

/**
 * /why-feelgreats — brand positioning. Concierge vs supplement store.
 */
export default function WhyFeelGreatsPage() {
  useSeo({
    title: "Why Feel Greats — Guided wellness, not supplement shopping",
    description:
      "We're not another supplement brand. We're a guided wellness concierge — assessment-first, mentor-supported, educational-first.",
    canonical: `${PROD_DOMAIN}/why-feelgreats`,
    ogType: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Why Feel Greats",
        "url": `${PROD_DOMAIN}/why-feelgreats`,
        "about": {
          "@type": "Organization",
          "name": "Feel Greats",
          "url": PROD_DOMAIN,
        },
      },
    ],
  });

  return (
    <TrustPage testid="why-page">
      <TrustHero
        eyebrow="Why Feel Greats"
        headline="The opposite of a supplement store."
        highlightWord="opposite"
        body="A typical wellness store wants you in the basket. We want you in the right conversation. We built Feel Greats for people who are tired of being sold to — and who'd rather be guided, gently, by someone who'll tell them the truth."
        primaryCta={{ to: "/questionnaire", label: "Start with the free assessment", testid: "why-hero-cta", icon: Sparkles }}
        secondaryCta={{ to: "/about", label: "Read our story", testid: "why-hero-about" }}
        testid="why-hero"
      />

      {/* The contrast */}
      <TrustSection
        eyebrow="The contrast"
        title="Where supplement brands stop, we begin."
        body="The wellness industry is loud, fast and packaged. We chose to be quiet, patient and personal. Here's the difference, in plain language."
        tone="default"
        testid="why-contrast"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <article className="rounded-3xl border border-stone-200 bg-stone-50/60 p-7" data-testid="why-typical">
            <p className="text-[10px] uppercase tracking-[0.22em] text-stone-500 font-semibold">A typical supplement store</p>
            <h3 className="font-display text-xl text-stone-700 font-light mt-1">Adds you to a basket.</h3>
            <ul className="mt-4 space-y-2 text-sm text-stone-600 leading-relaxed">
              <li>· Quizzes designed to push 5 products at once.</li>
              <li>· Stock photos and influencer testimonials.</li>
              <li>· Algorithmic upsells at checkout.</li>
              <li>· No human reachable after payment.</li>
              <li>· Hidden subscription locks and reorder traps.</li>
              <li>· Loud claims, weak ingredients, fast sales.</li>
            </ul>
          </article>
          <article className="rounded-3xl border border-emerald-300/70 bg-emerald-50/40 p-7" data-testid="why-feelgreats">
            <p className="text-[10px] uppercase tracking-[0.22em] text-emerald-700 font-semibold">Feel Greats</p>
            <h3 className="font-display text-xl text-emerald-950 font-light mt-1">Asks you a few honest questions, then guides you.</h3>
            <ul className="mt-4 space-y-2 text-sm text-emerald-900/85 leading-relaxed">
              <li>· An assessment that often ends in "not yet — try this habit first."</li>
              <li>· Calm copy, real ingredient PDFs, no influencer fluff.</li>
              <li>· Personalised picks ranked by your signals, not by margin.</li>
              <li>· A real mentor on WhatsApp before <em>and</em> after purchase.</li>
              <li>· Pause, adjust, cancel any time — visibly in your dashboard.</li>
              <li>· One or two products max, with a steady rhythm around them.</li>
            </ul>
          </article>
        </div>
      </TrustSection>

      {/* The four pillars */}
      <TrustSection
        eyebrow="Our four pillars"
        title="What 'guided wellness' actually means."
        tone="white"
        testid="why-pillars"
      >
        <IconCardGrid
          cols={4}
          cards={[
            { icon: Compass, title: "Personalised", body: "Your assessment shapes the recommendations. Nothing is generic. If your profile doesn't fit our products, we'll say so." },
            { icon: BookOpen, title: "Educational-first", body: "Every page should leave you a little smarter about your body — even if you never buy a thing. We publish a wellness library, not a sales catalogue." },
            { icon: HeartHandshake, title: "Mentor-supported", body: "A small UK-based team of certified mentors. Available on WhatsApp during working hours, and quietly checking in over time." },
            { icon: ShieldCheck, title: "Safety-aware", body: "We flag interactions, ask about medications, and never recommend during pregnancy without a clinician check. Wellness, never medical claims." },
          ]}
        />
      </TrustSection>

      {/* Long-term support */}
      <TrustSection
        eyebrow="Long-term support"
        title="We stay in your life longer than the bottle does."
        body="Most brands forget you after the order. We're built to do the opposite. Week 2 check-in. Week 6 nudge. Quarterly reassessment. Always optional — never pushy."
        tone="amber"
        testid="why-support"
      >
        <IconCardGrid
          cols={3}
          cards={[
            { icon: Users, title: "A small, loyal community.", body: "We'd rather have 5,000 members who feel quietly great than 500,000 who churn after a month." },
            { icon: Leaf, title: "A patient pace.", body: "Real change lands at week 4-12. We pace check-ins to that science, not to a sales calendar." },
            { icon: MessageCircle, title: "A real reply, every time.", body: "Whether it's WhatsApp or email, the reply you get is from a human you can name. No tickets, no bots." },
          ]}
        />
      </TrustSection>

      <ClosingCta
        eyebrow="Try it before you trust it"
        title="The assessment is the proof."
        body="Two minutes will tell you whether this feels different. If it does, we'd love to be the calm corner of your wellness journey."
      />
    </TrustPage>
  );
}
